import styles from './campaigns.module.css';
import campaignStyles from './campaign.module.css';
import { Photo } from '../../../components/photo';
import { ProgressBar } from '../../../components/progress-bar';
import { ProgressPill } from '../progress-pill';
import { Card } from '../../../components/card';
import { PageHead } from '../../../components/page-head';
// Student campaigns: list of campaigns visible to the student, with progress.
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type StudentCampaignSummary } from '../../../api/student/campaigns/types';
import { useStudentCampaigns } from '../../../api/student/campaigns/hooks';
import { campaignStatus, isCurrentCampaign } from '../../../api/student/campaigns/gating';
import { percentOf } from '../../../utils/percent';
import { StudentCampaignDetail } from './student-campaign-detail';
import type { StudentLocation, StudentNavigate } from '../student-location';
import { NextSessionBanner } from '../next-session-banner';
import tabs from '../filter-row.module.css';
import { xpFraction } from '../xp-fraction';

type CampaignFilter = 'current' | 'ended' | 'completed' | 'all';

/** Each filter's name on its tab: its key, written whole. */
const FILTER_KEY: Record<CampaignFilter, string> = {
  current: 'student.campaigns.filter.current',
  ended: 'student.campaigns.filter.ended',
  completed: 'student.campaigns.filter.completed',
  all: 'student.campaigns.filter.all',
};

/**
 * 'current' = active + upcoming (the default tab); 'ended' = past their end
 * and not completed, whose activities can still be finished; 'completed' =
 * what the student completed.
 */
function matchesFilter(c: StudentCampaignSummary, filter: CampaignFilter): boolean {
  if (filter === 'all') return true;
  const b = campaignStatus(c);
  if (filter === 'completed') return b === 'completed';
  if (filter === 'ended') return b === 'ended';
  return isCurrentCampaign(b);
}

interface StudentCampaignsPageProps {
  location: StudentLocation;
  navigate: StudentNavigate;
  back: () => void;
}

export function StudentCampaignsPage({ location, navigate, back }: StudentCampaignsPageProps) {
  const { t } = useTranslation();
  const query = useStudentCampaigns();
  // Memoised: `?? []` mints a new array every render, so any hook depending on
  // it re-runs on every render even when the data is unchanged.
  const campaigns = useMemo(() => query.data ?? [], [query.data]);
  const loading = query.isLoading;
  const error = query.isError
    ? (query.error instanceof Error ? query.error.message : t('requestErrors.load'))
    : null;
  const [filter, setFilter] = useState<CampaignFilter>('current');

  const counts = useMemo(() => {
    const c = { current: 0, ended: 0, completed: 0, all: campaigns.length };
    for (const x of campaigns) {
      const b = campaignStatus(x);
      if (isCurrentCampaign(b)) c.current++;
      else if (b === 'ended') c.ended++;
      else if (b === 'completed') c.completed++;
    }
    return c;
  }, [campaigns]);

  const visible = useMemo(() => {
    const list = campaigns.filter((c) => matchesFilter(c, filter));
    return [...list].sort(
      (a, b) => (a.startDateUtc ?? Number.POSITIVE_INFINITY) - (b.startDateUtc ?? Number.POSITIVE_INFINITY),
    );
  }, [campaigns, filter]);

  if (location.kind !== 'campaigns' && 'campaignId' in location) {
    return (
      <StudentCampaignDetail
        id={location.campaignId}
        location={location}
        navigate={navigate}
        back={back}
      />
    );
  }

  const handleOpen = (id: string) => navigate({ kind: 'campaign', campaignId: id });

  return (
    <div className="page">
      <NextSessionBanner navigate={navigate} />
      <PageHead sub={t('student.campaigns.subtitle')}>{t('student.campaigns.title')}</PageHead>

      <div className={tabs.filters}>
        {(['current', 'ended', 'completed', 'all'] as CampaignFilter[]).map((f) => (
          <button
            key={f}
            type="button"
            className={`${tabs.filter} ${filter === f ? tabs.filterActive : ''}`}
            onClick={() => setFilter(f)}
          >
            {t(FILTER_KEY[f])}<sup>{counts[f]}</sup>
          </button>
        ))}
      </div>

      {loading && <p className={`${styles.campaignsInfo}`}>{t('common.loading')}</p>}
      {error && <div className={`${styles.campaignsError}`}>{error}</div>}
      {!loading && !error && visible.length === 0 && (
        <p className={`${styles.campaignsEmpty}`}>{t('student.campaigns.empty')}</p>
      )}

      {visible.map((c) => {
        const bucket = campaignStatus(c);
        const locked = bucket === 'locked';
        const pct = percentOf(c.xpEarned, c.totalXp);
        return (
          <Card
            key={c.id}
            padded={false}
            className={`${styles.crow} ${locked ? styles.crowLocked : ''}`}
            onClick={() => handleOpen(c.id)}
          >
            <Photo className={`${styles.crow__art}`} src={c.imageUrl} />
            <div className={`${styles.crow__body}`}>
              <span className={`${styles.crow__co}`}>{c.gameName} · {c.worldName}</span>
              <h3 className={`${styles.crow__title}`}>{c.name}</h3>
              <div className={`${styles.crow__prog}`}>
                <ProgressBar value={pct} grow gold={bucket === 'completed'} />
                <span className={campaignStyles.crow__pct}>{pct}%</span>
              </div>
            </div>
            <div className={`${styles.crow__side}`}>
              <ProgressPill of="campaign" status={bucket} />
              <span className={`${styles.crow__xp}`}>⭐ {xpFraction(c.xpEarned, c.totalXp)}</span>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
